import React, { useState } from "react";
import { Package, BookOpen, Film, Settings2, Eye, Brain, Zap, Skull, ChevronRight, ChevronLeft, X } from "lucide-react";

const BG = "https://images.pexels.com/photos/4012056/pexels-photo-4012056.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PAGES = [
  {
    title: "Welcome, Jack.",
    subtitle: "A short briefing before you enter Blackwood Annex.",
    body: [
      "Every scene reads itself aloud in a clean voice. Whispers come through in a lower, closer one.",
      "You survive by making choices, not by winning fights. Some doors, once opened, stay open.",
      "This guide can be reopened anytime from the menu.",
    ],
    items: [
      { icon: Skull, title: "There is no combat.", text: "You are 14. Running, hiding, and trapping is the whole point." },
      { icon: ChevronRight, title: "Every choice matters.", text: "Some lock endings in. Some cost you a piece of yourself." },
    ],
  },
  {
    title: "The three meters.",
    subtitle: "Watch these. They change every ending.",
    body: [
      "You have three vital signs. They live in the top-right corner of every scene.",
    ],
    items: [
      { icon: Eye, title: "SANITY", text: "Drops when you see the monster, when you keep making cruel choices, when you push forward without pausing. Restore by playing family MEMORIES and reaching safe rooms.", meter: "sanity" },
      { icon: Brain, title: "COMPOSURE", text: "Your emotional control. Drops from grief, betrayal, and hearing your mother's voice. Restore by playing MEMORIES linked to family keepsakes.", meter: "composure" },
      { icon: Zap, title: "CORRUPTION", text: "The Entity's influence. Rises when you use Requiem power or push it away too hard. Never falls. Above 60, endings change.", meter: "corruption" },
    ],
  },
  {
    title: "How to bring sanity back.",
    subtitle: "You are not helpless.",
    body: [
      "Sanity feels like a countdown but it isn't. Every act of Jack remembering himself repairs him.",
    ],
    items: [
      { icon: Film,      title: "Play a MEMORY", text: "Every keepsake you collect (pocket watch, photo, recorder) unlocks a short flashback in the MEMORIES panel. Playing one restores composure permanently and gives back sanity." },
      { icon: BookOpen,  title: "Read your JOURNAL", text: "Reviewing what Jack knows steadies him. Discovering the truth costs sanity now — but it makes the final chapter possible." },
      { icon: Skull,     title: "Enter a SAFE ROOM", text: "David's workshop and Miriam's office are safe rooms. Ambient music softens, hallucinations pause. Rest there when you can." },
      { icon: ChevronRight, title: "Choose kindness once", text: "Not every choice needs to be cold. When Jack forgives — his parents, himself — the meter recovers a little." },
    ],
  },
  {
    title: "The four panels.",
    subtitle: "Top-right of the game screen.",
    body: [
      "You have four buttons. They are always available.",
    ],
    items: [
      { icon: Package,   title: "INVENTORY", text: "Everything Jack is carrying — flashlight, tools, keys, letters. Some choices only appear when you have the right item." },
      { icon: BookOpen,  title: "JOURNAL", text: "Every note, log, letter, and clue Jack writes down. This is your evidence trail." },
      { icon: Film,      title: "MEMORIES", text: "Unlocked flashbacks. Play them to restore composure. Once played they are marked ● SEEN so you can't farm the boost." },
      { icon: Settings2, title: "AUDIO", text: "Volume slider, music on/off, narration on/off, mute. Adjust anytime — the game remembers." },
    ],
  },
  {
    title: "One last thing.",
    subtitle: "Halloween Eve is not fair.",
    body: [
      "The Corruption learns from you. Repeating the same trick twice makes it smarter next time.",
      "If a scene shows red HOSTILE PRESENCE, do not linger. Pick a choice fast.",
      "The whispers you hear are generated live. They will never say the same thing twice.",
      "You are not required to reach the ending on your first run. There are four.",
    ],
    items: [],
    finish: true,
  },
];

export const HowToPlay = ({ onClose, embedded = false }) => {
  const [page, setPage] = useState(0);
  const p = PAGES[page];
  const isLast = page === PAGES.length - 1;

  const wrapper = embedded
    ? "fixed inset-0 z-50 flex items-center justify-center"
    : "relative min-h-screen w-full flex items-center justify-center overflow-hidden";

  return (
    <div className={wrapper} data-testid="howtoplay-screen">
      {embedded && <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={onClose} />}
      {!embedded && (
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${BG})`, filter: "brightness(0.22) saturate(0.5) contrast(1.2)" }} aria-hidden />
      )}
      {!embedded && <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-black/95" aria-hidden />}

      <div className="relative z-10 w-full max-w-4xl mx-4 md:mx-8 px-8 py-10 md:p-12 bg-black/85 border border-white/20" data-testid="howtoplay-panel">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-emergency animate-heartbeat" aria-hidden />
            <span className="font-mono text-[11px] tracking-[0.4em] uppercase text-white/60">
              // BRIEFING — {String(page + 1).padStart(2, "0")} / {String(PAGES.length).padStart(2, "0")}
            </span>
          </div>
          <button
            onClick={onClose}
            className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50 hover:text-emergency transition-colors flex items-center gap-2"
            data-testid="howtoplay-skip"
          >
            {embedded ? <X size={14} /> : "SKIP"}
          </button>
        </div>

        <h1 className="horror-title text-4xl md:text-5xl mb-2 text-white" data-testid="howtoplay-title">
          {p.title}
        </h1>
        <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-emergency/80 mb-6">
          {p.subtitle}
        </p>

        <div className="narrative text-white space-y-4 text-base md:text-lg mb-8 max-w-3xl" data-testid="howtoplay-body">
          {p.body.map((line, i) => (<p key={i}>{line}</p>))}
        </div>

        {p.items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
            {p.items.map((it, i) => {
              const meterColor =
                it.meter === "sanity" ? "text-white" :
                it.meter === "composure" ? "text-white" :
                it.meter === "corruption" ? "text-blood" :
                "text-emergency";
              const Icon = it.icon;
              return (
                <div key={i} className="border border-white/15 bg-black/70 p-5 flex gap-4" data-testid={`howtoplay-item-${i}`}>
                  <div className={`shrink-0 w-10 h-10 border border-white/25 flex items-center justify-center ${meterColor}`}>
                    <Icon size={18} strokeWidth={2} />
                  </div>
                  <div>
                    <p className={`font-mono text-[13px] tracking-[0.3em] uppercase mb-1 font-bold ${meterColor} crt-text`}>
                      {it.title}
                    </p>
                    <p className="narrative text-sm text-white/90 leading-relaxed">{it.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="term-btn flex-none disabled:opacity-30"
            data-testid="howtoplay-prev"
          >
            <span className="inline-flex items-center gap-2">
              <ChevronLeft size={14} /> BACK
            </span>
          </button>
          <div className="flex items-center gap-2">
            {PAGES.map((_, i) => (
              <span
                key={i}
                className={`w-2 h-2 border transition-colors ${i === page ? "bg-emergency border-emergency" : "border-white/30"}`}
              />
            ))}
          </div>
          {isLast ? (
            <button
              onClick={onClose}
              className="term-btn term-btn-danger flex-none"
              data-testid="howtoplay-start"
            >
              <span className="inline-flex items-center gap-2">
                ENTER THE FACILITY <ChevronRight size={14} />
              </span>
            </button>
          ) : (
            <button
              onClick={() => setPage((p) => Math.min(PAGES.length - 1, p + 1))}
              className="term-btn flex-none"
              data-testid="howtoplay-next"
            >
              <span className="inline-flex items-center gap-2">
                NEXT <ChevronRight size={14} />
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
